import { useCallback, useEffect, useMemo, useState } from "react";
import { useDialogo } from "./dialogo";

/**
 * Campañas a medida (22/10/2026): pedirlas, verlas antes de aprobar y
 * seguirlas.
 *
 * Jorge: «que el panel me deje pedirle cosas y sólo cuando le pida, por
 * ejemplo que mande a centros de estética de Bilbao, que lo haga y pueda
 * seguir el reporte». Y que las plantillas se puedan cambiar.
 *
 * El camino tiene tres pasos y en ninguno de los dos primeros sale nada:
 *
 *   1. Elegir sectores, sitios y mensaje, y BUSCAR (sin mandar).
 *   2. Ver cada negocio con el mensaje exacto que le llegaría, quitar los que
 *      no, y pedir el visto bueno.
 *   3. Aprobar (arriba o en Tareas pendientes). Sólo entonces sale, una
 *      localidad cada vez, al ritmo de Leadslink; y se puede pausar o parar.
 */

interface Sector {
  clave: string;
  nombre: string;
  grupo?: string;
}

interface Catalogo {
  sectores: Sector[];
  provincias: Record<string, string[]>;
  google: { eur: number; tope_eur: number; queda_eur: number } | null;
  busquedaNuevaHastaEur: number;
}

interface Negocio {
  phone: string;
  name: string | null;
  city: string;
  website?: string | null;
  bucket?: string | null;
  message: string;
  whatsapp_confirmed?: boolean;
  rating?: number | null;
  reviews?: number | null;
  sector?: string;
  sector_name?: string;
}

interface Linea {
  sector: string;
  sectorNombre: string;
  localidad: string;
  encontrados?: number;
  seleccionados?: number;
  yaContactados?: number;
  conWhatsapp?: number;
  deOtraLocalidad?: number;
  error?: string;
}

interface Preparacion {
  id: string;
  estado: "buscando" | "lista" | "fallo";
  creada: string;
  pedido: { sectores: Sector[]; localidades: string[]; porLocalidad: number };
  hechas: number;
  total: number;
  lineas: Linea[];
  seleccion: Negocio[];
  error?: string;
  aprobacion?: string;
  google?: { eur: number; tope_eur: number } | null;
}

interface Campana {
  campanaId: string;
  nombre: string;
  sectorNombre: string;
  total: number;
  tandas: number;
  tandasHechas: number;
  pedidos: number;
  estado: "en-marcha" | "esperando" | "pausada" | "parada" | "terminada";
  porQue: string | null;
  proxima: string | null;
  plantilla: string | null;
  desde: string;
  leadslink: {
    encolado: number;
    enviado: number;
    descartado: number;
    retirado: number;
    respuestas: number;
  } | null;
}

interface PlantillaGuardada {
  id: string;
  nombre: string;
  textos: Record<string, string>;
  creada: string | null;
  cambiada: string | null;
}

interface NegocioContactado {
  telefono: string;
  nombre: string | null;
  ciudad: string | null;
  sector: string | null;
  web: string | null;
  campana: string | null;
  estado: string;
  cuando: string | null;
  mensaje: string | null;
  interaccion: boolean;
  resultado: string | null;
  dijo: string | null;
}

interface Respuesta {
  nombre: string | null;
  telefono: string;
  ciudad: string | null;
  notas: string | null;
  resultado: string | null;
  fecha: string | null;
  campana: string | null;
  sector: string | null;
}

/** Los cuatro tipos de negocio de Leadslink, dichos como los ve Jorge. */
const TIPOS: { clave: string; nombre: string; quien: string }[] = [
  { clave: "WEB", nombre: "Sin web", quien: "Negocios que no tienen página web" },
  { clave: "SEO", nombre: "Web que no sale", quien: "Tienen web pero no salen en Google" },
  { clave: "OPTIMIZAR", nombre: "Web que va bien", quien: "Tienen web y salen, se puede mejorar" },
  { clave: "OTRO", nombre: "El resto", quien: "Los que no encajan en los otros tres" },
];

const HUECOS: [string, string][] = [
  ["{negocio}", "el nombre del negocio"],
  ["{ciudad}", "el pueblo"],
  ["{sectores}", "el sector en plural («dentistas»)"],
  ["{sector}", "el sector en singular"],
  ["{firma}", "quién firma"],
  ["{ficha}", "su ficha de Google: «de 4,6 estrellas y 38 reseñas»"],
];

async function json<T>(url: string, cuerpo?: unknown): Promise<T> {
  const r = await fetch(url, {
    method: cuerpo === undefined ? "GET" : "POST",
    credentials: "same-origin",
    headers: cuerpo === undefined ? {} : { "content-type": "application/json" },
    ...(cuerpo === undefined ? {} : { body: JSON.stringify(cuerpo) }),
  });
  const d = (await r.json().catch(() => ({}))) as T & { error?: string };
  if (!r.ok) throw new Error(d.error ?? `El servidor contestó ${r.status}`);
  return d;
}

/** Sin tildes ni mayúsculas: «estetica» tiene que encontrar «Estética». */
const limpio = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/**
 * Lo que se pide a Leadslink por cada pueblo. La provincia va detrás para que
 * Google no confunda dos pueblos del mismo nombre; en el mensaje sólo sale el
 * pueblo. Bizkaia va sin ella, como lo ha buscado siempre Leadslink.
 */
const localidadDe = (pueblo: string, provincia: string) =>
  provincia === "Bizkaia" || limpio(pueblo) === limpio(provincia) ? pueblo : `${pueblo}, ${provincia}`;
const soloElPueblo = (l: string) => l.split(",")[0]!.trim();
/** «provincia:Bizkaia» es la provincia entera: una búsqueda en toda ella. */
const PROVINCIA = "provincia:";
const nombreDeSitio = (l: string) =>
  l.startsWith(PROVINCIA) ? `Toda ${l.slice(PROVINCIA.length)}` : soloElPueblo(l);

const euros = (n: number) => n.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ═════════════════════════════════════════════════════════════════════════════

export function NuevaCampana({
  puedeMandar,
  alPedir,
}: {
  puedeMandar: boolean;
  alPedir: () => Promise<void>;
}) {
  const [cat, setCat] = useState<Catalogo | null>(null);
  const [falloCat, setFalloCat] = useState<string | null>(null);
  const [prep, setPrep] = useState<Preparacion | null>(null);

  const cargarCatalogo = useCallback(async () => {
    try {
      setCat(await json<Catalogo>("/api/leadslink/catalogo"));
      setFalloCat(null);
    } catch (e) {
      setFalloCat(String(e));
    }
  }, []);

  useEffect(() => {
    void cargarCatalogo();
    /* Si había una búsqueda sin pedir, se retoma: recargar no la pierde. */
    void json<Preparacion[]>("/api/leadslink/preparaciones")
      .then((ps) => {
        const viva = ps.find((p) => !p.aprobacion && p.estado !== "fallo");
        if (viva) setPrep(viva);
      })
      .catch(() => undefined);
  }, [cargarCatalogo]);

  /* Mientras busca, se pregunta cada tres segundos cómo va. */
  useEffect(() => {
    if (prep?.estado !== "buscando") return;
    const t = setInterval(() => {
      void json<Preparacion>(`/api/leadslink/preparacion/${prep.id}`)
        .then((p) => {
          setPrep(p);
          if (p.estado !== "buscando") void cargarCatalogo();
        })
        .catch(() => undefined);
    }, 3_000);
    return () => clearInterval(t);
  }, [prep?.id, prep?.estado, cargarCatalogo]);

  if (falloCat && !cat)
    return (
      <section className="bloque">
        <h2>Nueva campaña</h2>
        <p className="aviso">No se pudo leer el catálogo de Leadslink: {falloCat}</p>
      </section>
    );
  if (!cat)
    return (
      <section className="bloque apagado">
        <h2>Nueva campaña</h2>
        <p className="pie">Cargando sectores y provincias…</p>
      </section>
    );

  if (prep)
    return (
      <Resultado
        prep={prep}
        setPrep={setPrep}
        puedeMandar={puedeMandar}
        alPedir={alPedir}
        nueva={() => setPrep(null)}
      />
    );

  return <Formulario cat={cat} alEmpezar={setPrep} />;
}

// ── Paso 1: qué, dónde y con qué mensaje ────────────────────────────────────

const OTRA = "__otra";

function Formulario({ cat, alEmpezar }: { cat: Catalogo; alEmpezar: (p: Preparacion) => void }) {
  const [filtro, setFiltro] = useState("");
  const [sectores, setSectores] = useState<Sector[]>([]);
  const [provincia, setProvincia] = useState("Bizkaia");
  const [localidad, setLocalidad] = useState("");
  const [otra, setOtra] = useState("");
  const [sitios, setSitios] = useState<string[]>([]);
  const [porLocalidad, setPorLocalidad] = useState(5);
  /** «siempre», el id de una guardada, o «nueva» (escrita para esta campaña). */
  const [mensaje, setMensaje] = useState("siempre");
  const propio = mensaje === "nueva";
  const [guardadas, setGuardadas] = useState<PlantillaGuardada[]>([]);
  useEffect(() => {
    void json<{ plantillas: PlantillaGuardada[] }>("/api/leadslink/plantillas-guardadas")
      .then((d) => setGuardadas(d.plantillas))
      .catch(() => undefined);
  }, []);
  const guardada = guardadas.find((g) => g.id === mensaje) ?? null;
  const [yendo, setYendo] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  const [plantillas, setPlantillas] = useState<Record<string, string>>({});

  /* Los sectores por su grupo, sin los ya elegidos, y filtrados si se escribe. */
  const grupos = useMemo(() => {
    const m = new Map<string, Sector[]>();
    for (const s of cat.sectores) {
      if (sectores.some((e) => e.clave === s.clave)) continue;
      if (filtro.trim() && !limpio(s.nombre).includes(limpio(filtro))) continue;
      const g = s.grupo ?? "Otros";
      m.set(g, [...(m.get(g) ?? []), s]);
    }
    return [...m.entries()].map(([g, ss]) => [g, ss.sort((a, b) => a.nombre.localeCompare(b.nombre, "es"))] as const);
  }, [cat.sectores, sectores, filtro]);
  const quedan = grupos.reduce((n, [, ss]) => n + ss.length, 0);

  const provincias = Object.keys(cat.provincias);
  const pueblos = cat.provincias[provincia] ?? [];

  /** Lo que hay elegido en los desplegables ahora mismo, como sitio. */
  const elegidoAhora =
    localidad === OTRA
      ? otra.trim()
        ? otra.includes(",")
          ? otra.trim()
          : localidadDe(otra.trim(), provincia)
        : null
      : localidad
        ? localidadDe(localidad, provincia)
        : `${PROVINCIA}${provincia}`;
  /* Si no se ha añadido nada, vale lo que esté en los desplegables. */
  const donde = sitios.length ? sitios : elegidoAhora ? [elegidoAhora] : [];

  const busquedas = sectores.length * donde.length;
  const tope = cat.busquedaNuevaHastaEur;
  const g = cat.google;

  function anadir() {
    if (elegidoAhora && !sitios.includes(elegidoAhora)) setSitios([...sitios, elegidoAhora]);
    setOtra("");
  }

  async function buscar() {
    setYendo(true);
    setFallo(null);
    try {
      const p = await json<Preparacion>("/api/leadslink/preparar", {
        sectores: sectores.map((s) => s.clave),
        localidades: donde,
        porLocalidad,
        ...(propio ? { plantillas } : {}),
        ...(guardada ? { plantillas: guardada.textos, plantillaNombre: guardada.nombre } : {}),
      });
      alEmpezar(p);
    } catch (e) {
      setFallo(String(e).replace(/^Error: /, ""));
    } finally {
      setYendo(false);
    }
  }

  return (
    <section className="bloque ll-nueva">
      <h2>Nueva campaña</h2>

      <div className="ll-paso">
        <div className="ll-paso-titulo">
          <span className="ll-num">1</span> Tipo de negocio
        </div>
        <div className="ll-fila">
          <select
            className="ll-input ll-select-grande"
            value=""
            aria-label="Tipo de negocio"
            onChange={(e) => {
              const s = cat.sectores.find((x) => x.clave === e.target.value);
              if (s) setSectores([...sectores, s]);
              setFiltro("");
            }}
          >
            <option value="">
              {quedan === 0 ? "Ninguno se llama así" : sectores.length ? "Añadir otro tipo de negocio…" : "Elige el tipo de negocio…"}
            </option>
            {grupos.map(([grupo, ss]) => (
              <optgroup key={grupo} label={grupo}>
                {ss.map((s) => (
                  <option key={s.clave} value={s.clave}>
                    {s.nombre}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <input
            className="ll-input ll-filtro"
            type="search"
            placeholder="Filtrar: estética, dental…"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            aria-label="Filtrar la lista de tipos de negocio"
          />
        </div>
        {sectores.length > 0 && (
          <div className="ll-chips">
            {sectores.map((s) => (
              <button
                type="button"
                key={s.clave}
                className="ll-chip es-elegido"
                title="Quitar"
                onClick={() => setSectores(sectores.filter((x) => x.clave !== s.clave))}
              >
                {s.nombre} <span aria-hidden>×</span>
              </button>
            ))}
          </div>
        )}
        <p className="pie">
          {cat.sectores.length} tipos, cada uno una sola vez. Puedes elegir varios: un negocio que salga en dos solo
          recibe un mensaje.
        </p>
      </div>

      <div className="ll-paso">
        <div className="ll-paso-titulo">
          <span className="ll-num">2</span> Dónde
        </div>
        <div className="ll-donde-fila">
          <label className="ll-campo">
            <span>Provincia</span>
            <select
              className="ll-input"
              value={provincia}
              onChange={(e) => {
                setProvincia(e.target.value);
                setLocalidad("");
              }}
            >
              {provincias.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <label className="ll-campo">
            <span>Localidad</span>
            <select className="ll-input" value={localidad} onChange={(e) => setLocalidad(e.target.value)}>
              <option value="">Toda la provincia</option>
              {pueblos.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
              <option value={OTRA}>Otra localidad…</option>
            </select>
          </label>
          {localidad === OTRA && (
            <label className="ll-campo">
              <span>¿Cuál?</span>
              <input
                className="ll-input"
                type="text"
                placeholder={`Un pueblo de ${provincia}`}
                value={otra}
                onChange={(e) => setOtra(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") anadir();
                }}
              />
            </label>
          )}
          <button type="button" className="ll-boton-suave ll-anadir" disabled={!elegidoAhora} onClick={anadir}>
            + Añadir otro sitio
          </button>
        </div>
        <p className="pie">
          {localidad === ""
            ? `«Toda la provincia» es una búsqueda en toda ${provincia}: hasta 60 negocios, los que Google enseña primero. Para llegar a más, elige localidades.`
            : "Con una localidad, solo se escribe a negocios de esa localidad: los de los pueblos de al lado se quitan."}
        </p>
        {sitios.length > 0 && (
          <>
            <p className="ll-etiqueta">Se busca en ({sitios.length})</p>
            <div className="ll-chips">
              {sitios.map((l) => (
                <button
                  type="button"
                  key={l}
                  className="ll-chip es-elegido"
                  title={`${l.replace(PROVINCIA, "provincia ")} — quitar`}
                  onClick={() => setSitios(sitios.filter((x) => x !== l))}
                >
                  {nombreDeSitio(l)} <span aria-hidden>×</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="ll-paso">
        <div className="ll-paso-titulo">
          <span className="ll-num">3</span> Cuántos y qué mensaje
        </div>
        <label className="ll-fila ll-cuantos">
          <span>Negocios por sitio y tipo de negocio</span>
          <input
            className="ll-input ll-numero"
            type="number"
            min={1}
            max={15}
            value={porLocalidad}
            onChange={(e) => setPorLocalidad(Math.max(1, Math.min(15, Number(e.target.value) || 1)))}
          />
        </label>
        <label className="ll-campo">
          <span>Mensaje</span>
          <select className="ll-input" value={mensaje} onChange={(e) => setMensaje(e.target.value)}>
            <option value="siempre">Los de siempre</option>
            {guardadas.length > 0 && (
              <optgroup label="Tus plantillas">
                {guardadas.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.nombre}
                  </option>
                ))}
              </optgroup>
            )}
            <option value="nueva">Escribir uno solo para esta campaña…</option>
          </select>
        </label>
        {guardada ? (
          <div className="ll-ejemplos">
            {Object.entries(guardada.textos).map(([k, t]) => (
              <div key={k}>
                <p className="ll-etiqueta">{k === "TODOS" ? "Para todos" : TIPOS.find((x) => x.clave === k)?.nombre ?? k}</p>
                <div className="ll-burbuja">{t}</div>
              </div>
            ))}
            <p className="pie">Se edita en la pestaña «Mensajes». Verás cada mensaje, ya con el nombre del negocio, antes de aprobar.</p>
          </div>
        ) : propio ? (
          <EditorDePlantillas
            plantillas={plantillas}
            setPlantillas={setPlantillas}
            sector={sectores[0]?.nombre ?? "Restaurante"}
            ciudad={donde[0] && !donde[0].startsWith(PROVINCIA) ? donde[0] : "Bilbao"}
          />
        ) : (
          <p className="pie">
            Un mensaje para cada tipo de negocio (sin web, web que no sale en Google…), con variaciones para que
            no lleguen todos iguales. Los verás uno a uno antes de aprobar.
          </p>
        )}
      </div>

      <div className="ll-pie-form">
        <div className="ll-coste">
          {busquedas > 0 ? (
            <>
              <span>
                <strong>{sectores.map((s) => s.nombre).join(", ")}</strong> en{" "}
                <strong>{donde.map(nombreDeSitio).join(", ")}</strong>
              </span>
              <span className="pie">
                {busquedas} búsqueda{busquedas === 1 ? "" : "s"}, hasta {busquedas * porLocalidad} negocios. Cada
                búsqueda nueva, hasta {euros(tope)} € de Google; las repetidas salen gratis.
              </span>
            </>
          ) : (
            <span className="pie">Elige al menos un tipo de negocio.</span>
          )}
          {g && (
            <span className="ll-gasto" title="Precio de lista, sin descontar lo gratis de Google">
              Google este mes: {euros(g.eur)} € de {euros(g.tope_eur)} €
            </span>
          )}
        </div>
        <button
          type="button"
          className="principal"
          disabled={busquedas === 0 || yendo}
          onClick={() => void buscar()}
        >
          {yendo ? "Empezando…" : "Buscar negocios"}
        </button>
      </div>
      {fallo && <p className="aviso">{fallo}</p>}
      <p className="pie">
        Buscar <strong>no manda nada</strong>: encuentra los negocios, mira cuáles tienen WhatsApp y te enseña
        el mensaje de cada uno. Tú decides después.
      </p>
    </section>
  );
}

function EditorDePlantillas({
  plantillas,
  setPlantillas,
  sector,
  ciudad,
}: {
  plantillas: Record<string, string>;
  setPlantillas: (p: Record<string, string>) => void;
  sector: string;
  ciudad: string;
}) {
  const [defecto, setDefecto] = useState<Record<string, string> | null>(null);
  const [firma, setFirma] = useState("");
  const [unaSola, setUnaSola] = useState(() => Boolean(plantillas.TODOS));
  const [tipo, setTipo] = useState("WEB");
  const [previa, setPrevia] = useState<{ errores: string[]; ejemplos: string[] } | null>(null);

  useEffect(() => {
    void json<{ plantillas: Record<string, string>; firma: string }>("/api/leadslink/plantillas")
      .then((d) => {
        setDefecto(d.plantillas);
        setFirma(d.firma);
        if (Object.keys(plantillas).length === 0) setPlantillas({ ...d.plantillas });
      })
      .catch(() => undefined);
    // Sólo al abrir el editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clave = unaSola ? "TODOS" : tipo;
  const texto = plantillas[clave] ?? (unaSola ? defecto?.OTRO ?? "" : defecto?.[tipo] ?? "");

  function cambiarModo(sola: boolean) {
    setUnaSola(sola);
    setPrevia(null);
    if (sola) setPlantillas({ TODOS: plantillas.TODOS ?? defecto?.OTRO ?? "" });
    else setPlantillas({ ...(defecto ?? {}) });
  }

  async function verPrevia() {
    try {
      setPrevia(await json("/api/leadslink/plantilla-previa", { plantilla: texto, sector, ciudad }));
    } catch (e) {
      setPrevia({ errores: [String(e)], ejemplos: [] });
    }
  }

  if (!defecto) return <p className="pie">Cargando los mensajes de siempre…</p>;

  return (
    <div className="ll-editor">
      <div className="ll-fila ll-entre">
        <div className="ll-segmentos ll-segmentos-peque">
          <button type="button" className={!unaSola ? "es-activo" : ""} onClick={() => cambiarModo(false)}>
            Uno por tipo de negocio
          </button>
          <button type="button" className={unaSola ? "es-activo" : ""} onClick={() => cambiarModo(true)}>
            Uno para todos
          </button>
        </div>
        <span className="pie">Firma: {firma}</span>
      </div>
      {!unaSola && (
        <div className="ll-pestanas" role="tablist">
          {TIPOS.map((t) => (
            <button
              type="button"
              role="tab"
              key={t.clave}
              aria-selected={tipo === t.clave}
              className={tipo === t.clave ? "es-activo" : ""}
              title={t.quien}
              onClick={() => {
                setTipo(t.clave);
                setPrevia(null);
              }}
            >
              {t.nombre}
              {plantillas[t.clave] !== undefined && plantillas[t.clave] !== defecto[t.clave] && (
                <span className="ll-punto" aria-label="cambiado" />
              )}
            </button>
          ))}
        </div>
      )}
      {!unaSola && <p className="pie">{TIPOS.find((t) => t.clave === tipo)?.quien}.</p>}
      <textarea
        className="ll-texto"
        rows={6}
        value={texto}
        onChange={(e) => {
          setPlantillas({ ...plantillas, [clave]: e.target.value });
          setPrevia(null);
        }}
      />
      <div className="ll-huecos">
        {HUECOS.map(([h, que]) => (
          <button
            type="button"
            key={h}
            className="ll-hueco"
            title={`Añadir ${h}: ${que}`}
            onClick={() => setPlantillas({ ...plantillas, [clave]: `${texto}${texto.endsWith(" ") ? "" : " "}${h}` })}
          >
            {h}
          </button>
        ))}
        <span className="pie">
          y variaciones entre llaves: <code>{"{Hola|Buenas}"}</code> elige una en cada mensaje.
        </span>
      </div>
      <div className="ll-fila">
        <button type="button" className="ll-boton-suave" onClick={() => void verPrevia()}>
          Ver cómo queda
        </button>
        {!unaSola && plantillas[tipo] !== defecto[tipo] && (
          <button
            type="button"
            className="ll-boton-suave"
            onClick={() => setPlantillas({ ...plantillas, [tipo]: defecto[tipo] ?? "" })}
          >
            Volver al de siempre
          </button>
        )}
      </div>
      {previa && previa.errores.length > 0 && (
        <ul className="ll-errores">
          {previa.errores.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
      {previa && previa.ejemplos.length > 0 && (
        <div className="ll-ejemplos">
          {previa.ejemplos.map((e, i) => (
            <div className="ll-burbuja" key={i}>
              {e}
            </div>
          ))}
        </div>
      )}
      <p className="pie">
        Sin enlaces: en un primer mensaje hacen que parezca publicidad y es lo que más denuncias trae.
      </p>
    </div>
  );
}

// ── Paso 2: lo encontrado, negocio a negocio ────────────────────────────────

function Resultado({
  prep,
  setPrep,
  puedeMandar,
  alPedir,
  nueva,
}: {
  prep: Preparacion;
  setPrep: (p: Preparacion) => void;
  puedeMandar: boolean;
  alPedir: () => Promise<void>;
  nueva: () => void;
}) {
  const [fuera, setFuera] = useState<Set<string>>(new Set());
  const [abierto, setAbierto] = useState<string | null>(null);
  const [nombre, setNombre] = useState("");
  const [porQue, setPorQue] = useState("");
  const [yendo, setYendo] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);

  const grupos = useMemo(() => {
    const m = new Map<string, Negocio[]>();
    for (const n of prep.seleccion) {
      const k = `${soloElPueblo(n.city)} · ${n.sector_name ?? ""}`;
      m.set(k, [...(m.get(k) ?? []), n]);
    }
    return [...m.entries()];
  }, [prep.seleccion]);

  const quedan = prep.seleccion.filter((n) => !fuera.has(n.phone)).length;
  const sugerido = `${prep.pedido.sectores.map((s) => s.nombre).join(", ")} en ${prep.pedido.localidades
    .map(nombreDeSitio)
    .slice(0, 3)
    .join(", ")}${prep.pedido.localidades.length > 3 ? "…" : ""}`;

  function alternar(phone: string) {
    const f = new Set(fuera);
    if (f.has(phone)) f.delete(phone);
    else f.add(phone);
    setFuera(f);
  }

  async function pedir() {
    setYendo(true);
    setFallo(null);
    try {
      const r = await json<{ aprobacion: string }>(`/api/leadslink/preparacion/${prep.id}/pedir`, {
        quitar: [...fuera],
        nombre,
        porQue,
      });
      setPrep({ ...prep, aprobacion: r.aprobacion });
      await alPedir();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setFallo(String(e).replace(/^Error: /, ""));
    } finally {
      setYendo(false);
    }
  }

  const buscando = prep.estado === "buscando";
  const pct = prep.total ? Math.round((prep.hechas / prep.total) * 100) : 0;

  return (
    <section className={`bloque ll-nueva ${prep.aprobacion ? "" : "urgente"}`}>
      <h2 className="ll-h2-con-boton">
        <span>{buscando ? "Buscando negocios…" : prep.aprobacion ? "Campaña pedida" : "Lo que ha encontrado"}</span>
        {!buscando && (
          <button type="button" className="ll-boton-suave" onClick={nueva}>
            Nueva búsqueda
          </button>
        )}
      </h2>

      <div className="ll-progreso" aria-label={`${prep.hechas} de ${prep.total}`}>
        <div className="ll-progreso-relleno" style={{ width: `${buscando ? Math.max(4, pct) : 100}%` }} />
      </div>
      <p className="pie">
        {buscando
          ? `${prep.hechas} de ${prep.total} búsquedas. Cada pueblo tarda entre medio minuto y dos: mira las webs y pregunta a WhatsApp, despacio. Puedes irte; esto sigue.`
          : `${prep.total} búsqueda(s) hechas. Nada se ha mandado.`}
        {prep.google && ` Google este mes: ${euros(prep.google.eur)} € de ${euros(prep.google.tope_eur)} €.`}
      </p>

      <ul className="ll-lineas">
        {prep.lineas.map((l, i) => (
          <li key={i} className={l.error ? "es-mal" : ""}>
            <span className="ll-donde">
              {nombreDeSitio(l.localidad)} · {l.sectorNombre}
            </span>
            {l.error ? (
              <span className="ll-dato">{l.error}</span>
            ) : l.encontrados === undefined ? (
              <span className="ll-dato pie">buscando…</span>
            ) : (
              <span className="ll-dato">
                {l.encontrados} encontrados → <strong>{l.seleccionados}</strong> elegidos
                {(l.conWhatsapp ?? 0) > 0 && ` (${l.conWhatsapp} con WhatsApp comprobado)`}
                {(l.yaContactados ?? 0) > 0 && ` · ${l.yaContactados} ya contactados, fuera`}
                {(l.deOtraLocalidad ?? 0) > 0 && ` · ${l.deOtraLocalidad} de otros pueblos, fuera`}
              </span>
            )}
          </li>
        ))}
      </ul>
      {prep.error && <p className="aviso">{prep.error}</p>}

      {!buscando && prep.seleccion.length === 0 && (
        <p className="aviso">
          No ha salido ningún negocio con WhatsApp. Prueba otro pueblo, otro sector parecido o más negocios por
          pueblo.
        </p>
      )}

      {grupos.length > 0 && (
        <div className="ll-lista">
          {grupos.map(([donde, ns]) => (
            <div className="ll-grupo" key={donde}>
              <div className="ll-grupo-titulo">
                {donde} <span className="pie">({ns.length})</span>
              </div>
              {ns.map((n) => {
                const dentro = !fuera.has(n.phone);
                const ver = abierto === n.phone;
                return (
                  <div className={`ll-negocio ${dentro ? "" : "es-fuera"}`} key={n.phone}>
                    <label className="ll-negocio-fila">
                      <input
                        type="checkbox"
                        checked={dentro}
                        disabled={Boolean(prep.aprobacion)}
                        onChange={() => alternar(n.phone)}
                        aria-label={`Incluir ${n.name ?? n.phone}`}
                      />
                      <span className="ll-negocio-nombre">{n.name ?? n.phone}</span>
                      <span className={`ll-wa ${n.whatsapp_confirmed ? "es-ok" : "es-aviso"}`}>
                        {n.whatsapp_confirmed ? "WhatsApp ✓" : "sin comprobar"}
                      </span>
                      <span className="ll-negocio-dato">{n.phone}</span>
                      {n.rating ? (
                        <span className="ll-negocio-dato">
                          ★ {n.rating} ({n.reviews ?? 0})
                        </span>
                      ) : null}
                      <span className="ll-negocio-dato">{n.website ? "con web" : "sin web"}</span>
                    </label>
                    <button type="button" className="ll-ver" onClick={() => setAbierto(ver ? null : n.phone)}>
                      {ver ? "Ocultar mensaje" : "Ver mensaje"}
                    </button>
                    {ver && (
                      <div className="ll-burbuja">
                        {n.message}
                        {n.website && (
                          <a className="ll-enlace" href={n.website} target="_blank" rel="noreferrer">
                            Abrir su web
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {!buscando && quedan > 0 && !prep.aprobacion && (
        <div className="ll-pedir">
          <label className="ll-campo">
            <span>Nombre de la campaña</span>
            <input className="ll-input" type="text" placeholder={sugerido} value={nombre} onChange={(e) => setNombre(e.target.value)} />
          </label>
          <label className="ll-campo">
            <span>Por qué (opcional)</span>
            <input
              className="ll-input"
              type="text"
              placeholder="para acordarte dentro de un mes"
              value={porQue}
              onChange={(e) => setPorQue(e.target.value)}
            />
          </label>
          <div className="ll-pie-form">
            <span className="pie">
              {fuera.size > 0 && `${fuera.size} quitado(s). `}
              {!puedeMandar && "El enviador está parado: lo aprobado esperará a que vuelva."}
            </span>
            <button type="button" className="principal" disabled={yendo} onClick={() => void pedir()}>
              {yendo ? "Pidiendo…" : `Pedir el visto bueno · ${quedan} mensaje${quedan === 1 ? "" : "s"}`}
            </button>
          </div>
          {fallo && <p className="aviso">{fallo}</p>}
        </div>
      )}
      {prep.aprobacion && (
        <p className="ll-hecho">
          Pedida. La apruebas arriba, en «Esperan tu visto bueno», o en Tareas pendientes. Hasta entonces no sale
          nada.
        </p>
      )}
    </section>
  );
}

// ── Paso 3: seguirlas ───────────────────────────────────────────────────────

const ESTADOS: Record<Campana["estado"], { texto: string; clase: string }> = {
  "en-marcha": { texto: "Mandando", clase: "es-ok" },
  esperando: { texto: "Esperando turno", clase: "es-ok" },
  pausada: { texto: "Pausada", clase: "es-aviso" },
  parada: { texto: "Parada", clase: "es-mal" },
  terminada: { texto: "Terminada", clase: "es-apagado" },
};

const hora = (s: string) =>
  new Date(s).toLocaleString("es-ES", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export function CampanasYRespuestas({ solo }: { solo?: "campanas" | "respuestas" } = {}) {
  const [campanas, setCampanas] = useState<Campana[] | null>(null);
  const [respuestas, setRespuestas] = useState<Respuesta[] | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [yendo, setYendo] = useState<string | null>(null);
  const { preguntar, dialogo } = useDialogo();

  const cargar = useCallback(async () => {
    const [c, r] = await Promise.allSettled([
      json<{ campanas: Campana[] }>("/api/leadslink/campanas"),
      json<{ respuestas: Respuesta[] }>("/api/leadslink/respuestas"),
    ]);
    if (c.status === "fulfilled") setCampanas(c.value.campanas);
    if (r.status === "fulfilled") setRespuestas(r.value.respuestas);
  }, []);

  useEffect(() => {
    void cargar();
    const t = setInterval(() => void cargar(), 15_000);
    return () => clearInterval(t);
  }, [cargar]);

  async function ordenar(c: Campana, orden: "pausar" | "reanudar" | "parar") {
    if (
      orden === "parar" &&
      !(await preguntar(
        `¿Parar «${c.nombre}» para siempre? Lo que espera turno no saldrá. Lo ya enviado no se retira.`,
        "Parar",
        true,
      ))
    )
      return;
    setYendo(c.campanaId);
    setFallo(null);
    try {
      await json(`/api/leadslink/campanas/${c.campanaId}/${orden}`, {});
      await cargar();
    } catch (e) {
      setFallo(String(e).replace(/^Error: /, ""));
    } finally {
      setYendo(null);
    }
  }

  async function marcar(r: Respuesta, resultado: string) {
    setYendo(r.telefono);
    try {
      await json("/api/leadslink/respuestas/resultado", { phone: r.telefono, resultado });
      await cargar();
    } catch (e) {
      setFallo(String(e).replace(/^Error: /, ""));
    } finally {
      setYendo(null);
    }
  }

  const nombreDe = new Map((campanas ?? []).map((c) => [c.campanaId, c.nombre]));

  return (
    <>
      {dialogo}
      {solo !== "respuestas" && (
      <section className={`bloque ${campanas && campanas.length === 0 ? "apagado" : ""}`}>
        <h2>Campañas</h2>
        {campanas === null ? (
          <p className="pie">Mirando…</p>
        ) : campanas.length === 0 ? (
          <p className="pie">Todavía no hay ninguna. Se piden en «Nueva campaña» o por el chat.</p>
        ) : (
          <div className="ll-campanas">
            {campanas.map((c) => {
              const e = ESTADOS[c.estado];
              const l = c.leadslink;
              const viva = c.estado === "en-marcha" || c.estado === "esperando";
              return (
                <article className="ll-campana" key={c.campanaId}>
                  <header className="ll-campana-cabeza">
                    <div>
                      <div className="ll-campana-nombre">{c.nombre}</div>
                      <div className="pie">
                        Desde {hora(c.desde)} · {c.total} aprobados en {c.tandas} tanda{c.tandas === 1 ? "" : "s"}
                        {c.plantilla && ` · mensaje: ${c.plantilla}`}
                      </div>
                    </div>
                    <span className={`ll-estado ${e.clase}`}>{e.texto}</span>
                  </header>
                  <div className="ll-progreso">
                    <div
                      className="ll-progreso-relleno"
                      style={{ width: `${c.total ? Math.min(100, Math.round(((l?.enviado ?? 0) / c.total) * 100)) : 0}%` }}
                    />
                  </div>
                  <ul className="ll-cuentas">
                    <li>
                      <strong>{l?.enviado ?? "—"}</strong> enviados
                    </li>
                    <li>
                      <strong>{l?.encolado ?? "—"}</strong> esperan turno
                    </li>
                    <li>
                      <strong>{c.tandas - c.tandasHechas}</strong> tandas por pedir
                    </li>
                    <li>
                      <strong>{l?.descartado ?? "—"}</strong> sin WhatsApp
                    </li>
                    <li className={l && l.respuestas > 0 ? "es-ok" : ""}>
                      <strong>{l?.respuestas ?? "—"}</strong> respuestas
                    </li>
                  </ul>
                  {(c.porQue || c.proxima) && (
                    <p className="pie">
                      {c.porQue}
                      {c.proxima && viva && ` Próxima vuelta: ${hora(c.proxima)}.`}
                    </p>
                  )}
                  {(viva || c.estado === "pausada") && (
                    <div className="ll-fila">
                      {viva && (
                        <button type="button" className="ll-boton-suave" disabled={yendo === c.campanaId} onClick={() => void ordenar(c, "pausar")}>
                          Pausar
                        </button>
                      )}
                      {c.estado === "pausada" && (
                        <button type="button" className="ll-boton-suave" disabled={yendo === c.campanaId} onClick={() => void ordenar(c, "reanudar")}>
                          Reanudar
                        </button>
                      )}
                      <button type="button" className="ll-boton-suave es-peligro" disabled={yendo === c.campanaId} onClick={() => void ordenar(c, "parar")}>
                        Parar
                      </button>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
        {fallo && <p className="aviso">{fallo}</p>}
      </section>
      )}

      {solo !== "campanas" && (
      <section className={`bloque ${respuestas && respuestas.length === 0 ? "apagado" : ""}`}>
        <h2>Respuestas{respuestas && respuestas.length > 0 ? ` (${respuestas.length})` : ""}</h2>
        {respuestas === null ? (
          <p className="pie">No se pudo preguntar a Leadslink.</p>
        ) : respuestas.length === 0 ? (
          <p className="pie">Nadie ha contestado todavía.</p>
        ) : (
          <ul className="ll-respuestas">
            {respuestas.map((r) => {
              const dijo = (r.notas ?? "").trim().split("\n").pop()?.replace(/^\[[^\]]*\]\s*Respondió:?\s*/, "");
              return (
                <li key={r.telefono} className="ll-respuesta">
                  <div className="ll-respuesta-cabeza">
                    <span className="ll-negocio-nombre">{r.nombre ?? r.telefono}</span>
                    <span className="pie">
                      {[r.ciudad, r.campana ? nombreDe.get(r.campana) ?? null : null].filter(Boolean).join(" · ")}
                    </span>
                    <EstadoDeRespuesta resultado={r.resultado} />
                  </div>
                  {dijo && <div className="ll-dijo">«{dijo}»</div>}
                  <div className="ll-fila">
                    <a className="ll-boton-suave" href={`https://wa.me/34${r.telefono}`} target="_blank" rel="noreferrer">
                      Abrir el chat
                    </a>
                    {(
                      [
                        ["interesado", "Interesado"],
                        ["cliente", "Ya es cliente"],
                        ["no_interesa", "No le interesa"],
                      ] as const
                    ).map(([k, t]) => (
                      <button
                        type="button"
                        key={k}
                        className={`ll-boton-suave ${r.resultado === k ? "es-activo" : ""}`}
                        disabled={yendo === r.telefono || r.resultado === k}
                        onClick={() => void marcar(r, k)}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <p className="pie">
          «No le interesa» lo saca de toda campaña futura; si contesta «no», «baja» o parecido, se hace solo.
        </p>
        {fallo && solo === "respuestas" && <p className="aviso">{fallo}</p>}
      </section>
      )}
    </>
  );
}

function EstadoDeRespuesta({ resultado }: { resultado: string | null }) {
  const m: Record<string, [string, string]> = {
    respondido: ["Por contestar", "es-aviso"],
    pendiente: ["Por contestar", "es-aviso"],
    interesado: ["Interesado", "es-ok"],
    cliente: ["Cliente", "es-ok"],
    no_interesa: ["No le interesa", "es-apagado"],
    hable: ["Hablado, no compró", "es-apagado"],
  };
  const [t, c] = m[resultado ?? ""] ?? ["—", "es-apagado"];
  return <span className={`ll-estado ${c}`}>{t}</span>;
}

// ═════════════════════════════════════════════════════════════════════════════
// Mensajes: las plantillas guardadas (23/10/2026)
//
// Jorge: «no veo las plantillas ni forma de modificarlas o crear nuevas para
// asignarlas a las campañas». Aquí se ven las de siempre, se crean, se cambian
// y se borran las suyas; en «Nueva campaña» se elige cuál lleva cada una.
// ═════════════════════════════════════════════════════════════════════════════

export function GestorDeMensajes() {
  const [datos, setDatos] = useState<{
    plantillas: PlantillaGuardada[];
    por_defecto: Record<string, string>;
    firma: string;
  } | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [editando, setEditando] = useState<{ id?: string; nombre: string; textos: Record<string, string> } | null>(null);
  const [guardando, setGuardando] = useState(false);
  const { preguntar, dialogo } = useDialogo();

  const cargar = useCallback(async () => {
    try {
      setDatos(await json("/api/leadslink/plantillas-guardadas"));
      setFallo(null);
    } catch (e) {
      setFallo(String(e).replace(/^Error: /, ""));
    }
  }, []);
  useEffect(() => void cargar(), [cargar]);

  async function guardar() {
    if (!editando) return;
    setGuardando(true);
    setFallo(null);
    try {
      await json("/api/leadslink/plantillas-guardadas", editando);
      setEditando(null);
      await cargar();
    } catch (e) {
      setFallo(String(e).replace(/^Error: /, ""));
    } finally {
      setGuardando(false);
    }
  }

  async function borrar(p: PlantillaGuardada) {
    if (!(await preguntar(`¿Borrar la plantilla «${p.nombre}»? Las campañas ya pedidas no cambian.`, "Borrar", true))) return;
    try {
      await json(`/api/leadslink/plantillas-guardadas/${p.id}/borrar`, {});
      await cargar();
    } catch (e) {
      setFallo(String(e).replace(/^Error: /, ""));
    }
  }

  if (!datos)
    return (
      <section className="bloque apagado">
        <h2>Mensajes</h2>
        <p className="pie">{fallo ?? "Cargando…"}</p>
      </section>
    );

  if (editando)
    return (
      <section className="bloque ll-nueva">
        <h2>{editando.id ? "Cambiar la plantilla" : "Nueva plantilla"}</h2>
        <label className="ll-campo">
          <span>Nombre</span>
          <input
            className="ll-input"
            type="text"
            placeholder="Por ejemplo: Estética, tono cercano"
            value={editando.nombre}
            onChange={(e) => setEditando({ ...editando, nombre: e.target.value })}
          />
        </label>
        <EditorDePlantillas
          plantillas={editando.textos}
          setPlantillas={(t) => setEditando({ ...editando, textos: t })}
          sector="Centro de Estética"
          ciudad="Bilbao"
        />
        <div className="ll-pie-form">
          <button type="button" className="ll-boton-suave" onClick={() => setEditando(null)}>
            Cancelar
          </button>
          <button type="button" className="principal" disabled={guardando || !editando.nombre.trim()} onClick={() => void guardar()}>
            {guardando ? "Guardando…" : "Guardar plantilla"}
          </button>
        </div>
        {fallo && <p className="aviso">{fallo}</p>}
      </section>
    );

  return (
    <>
      {dialogo}
      <section className="bloque">
        <h2 className="ll-h2-con-boton">
          <span>Tus plantillas ({datos.plantillas.length})</span>
          <button type="button" className="ll-boton-suave" onClick={() => setEditando({ nombre: "", textos: {} })}>
            + Nueva plantilla
          </button>
        </h2>
        {datos.plantillas.length === 0 ? (
          <p className="pie">
            Todavía no has guardado ninguna. Crea una y la podrás elegir en «Nueva campaña». Firma:{" "}
            {datos.firma}.
          </p>
        ) : (
          <div className="ll-campanas">
            {datos.plantillas.map((p) => (
              <article className="ll-campana" key={p.id}>
                <header className="ll-campana-cabeza">
                  <div>
                    <div className="ll-campana-nombre">{p.nombre}</div>
                    <div className="pie">
                      {p.textos.TODOS ? "Un mensaje para todos" : `${Object.keys(p.textos).length} mensajes, uno por tipo de negocio`}
                      {p.cambiada && ` · cambiada ${hora(p.cambiada)}`}
                    </div>
                  </div>
                  <div className="ll-fila">
                    <button
                      type="button"
                      className="ll-boton-suave"
                      onClick={() => setEditando({ id: p.id, nombre: p.nombre, textos: { ...p.textos } })}
                    >
                      Cambiar
                    </button>
                    <button type="button" className="ll-boton-suave es-peligro" onClick={() => void borrar(p)}>
                      Borrar
                    </button>
                  </div>
                </header>
                <div className="ll-ejemplos">
                  {Object.entries(p.textos).map(([k, t]) => (
                    <div key={k}>
                      <p className="ll-etiqueta">{k === "TODOS" ? "Para todos" : TIPOS.find((x) => x.clave === k)?.nombre ?? k}</p>
                      <div className="ll-burbuja">{t}</div>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        )}
        {fallo && <p className="aviso">{fallo}</p>}
      </section>

      <section className="bloque">
        <h2 className="ll-h2-con-boton">
          <span>Los de siempre</span>
          <button
            type="button"
            className="ll-boton-suave"
            onClick={() => setEditando({ nombre: "Copia de los de siempre", textos: { ...datos.por_defecto } })}
          >
            Copiar como plantilla nueva
          </button>
        </h2>
        <p className="pie">Los que se usan si no eliges otra. Uno por tipo de negocio, con variaciones entre llaves.</p>
        <div className="ll-ejemplos">
          {TIPOS.map((t) => (
            <div key={t.clave}>
              <p className="ll-etiqueta">
                {t.nombre} — {t.quien.toLowerCase()}
              </p>
              <div className="ll-burbuja">{datos.por_defecto[t.clave]}</div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Negocios: los que han contestado y los que no (23/10/2026)
// ═════════════════════════════════════════════════════════════════════════════

export function NegociosContactados() {
  const [negocios, setNegocios] = useState<NegocioContactado[] | null>(null);
  const [nombres, setNombres] = useState<Map<string, string>>(new Map());
  const [fallo, setFallo] = useState<string | null>(null);
  const [vista, setVista] = useState<"con" | "sin">("con");
  const [campana, setCampana] = useState("");
  const [busca, setBusca] = useState("");
  const [yendo, setYendo] = useState<string | null>(null);
  const [abierto, setAbierto] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    try {
      const [n, c] = await Promise.all([
        json<{ negocios: NegocioContactado[] }>("/api/leadslink/negocios"),
        json<{ campanas: Campana[] }>("/api/leadslink/campanas").catch(() => ({ campanas: [] as Campana[] })),
      ]);
      setNegocios(n.negocios);
      setNombres(new Map(c.campanas.map((x) => [x.campanaId, x.nombre])));
      setFallo(null);
    } catch (e) {
      setFallo(String(e).replace(/^Error: /, ""));
    }
  }, []);
  useEffect(() => {
    void cargar();
    const t = setInterval(() => void cargar(), 30_000);
    return () => clearInterval(t);
  }, [cargar]);

  async function marcar(n: NegocioContactado, resultado: string) {
    setYendo(n.telefono);
    try {
      await json("/api/leadslink/respuestas/resultado", { phone: n.telefono, resultado });
      await cargar();
    } catch (e) {
      setFallo(String(e).replace(/^Error: /, ""));
    } finally {
      setYendo(null);
    }
  }

  if (!negocios)
    return (
      <section className="bloque apagado">
        <h2>Negocios</h2>
        <p className="pie">{fallo ?? "Mirando…"}</p>
      </section>
    );

  const deLaCampana = negocios.filter((n) => !campana || (n.campana ?? "") === campana);
  const con = deLaCampana.filter((n) => n.interaccion);
  const sin = deLaCampana.filter((n) => !n.interaccion);
  const lista = (vista === "con" ? con : sin).filter(
    (n) => !busca.trim() || limpio(`${n.nombre ?? ""} ${n.ciudad ?? ""} ${n.sector ?? ""}`).includes(limpio(busca)),
  );
  const campanas = [...new Set(negocios.map((n) => n.campana ?? ""))];

  return (
    <section className="bloque">
      <h2>Negocios a los que se ha escrito ({deLaCampana.length})</h2>
      <div className="ll-fila ll-entre">
        <div className="ll-segmentos" role="tablist">
          <button type="button" role="tab" aria-selected={vista === "con"} className={vista === "con" ? "es-activo" : ""} onClick={() => setVista("con")}>
            Han contestado ({con.length})
          </button>
          <button type="button" role="tab" aria-selected={vista === "sin"} className={vista === "sin" ? "es-activo" : ""} onClick={() => setVista("sin")}>
            Sin respuesta ({sin.length})
          </button>
        </div>
        <div className="ll-fila">
          <select className="ll-input" value={campana} onChange={(e) => setCampana(e.target.value)} aria-label="Campaña">
            <option value="">Todas las campañas</option>
            {campanas.map((c) => (
              <option key={c || "ninguna"} value={c}>
                {c ? nombres.get(c) ?? c : "Sin campaña (a mano)"}
              </option>
            ))}
          </select>
          <input className="ll-input" type="search" placeholder="Buscar negocio o pueblo" value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
      </div>

      {lista.length === 0 ? (
        <p className="pie">
          {negocios.length === 0
            ? "Todavía no ha salido ningún mensaje de campaña. Cuando salgan, aquí verás a quién se escribió y quién contestó."
            : vista === "con"
              ? "Nadie de aquí ha contestado todavía."
              : "Todos los de aquí han contestado."}
        </p>
      ) : (
        <ul className="ll-respuestas">
          {lista.map((n) => (
            <li key={n.telefono} className="ll-respuesta">
              <div className="ll-respuesta-cabeza">
                <span className="ll-negocio-nombre">{n.nombre ?? n.telefono}</span>
                <span className="pie">
                  {[n.ciudad, n.sector, n.campana ? nombres.get(n.campana) ?? null : n.estado === "a mano" ? "a mano" : null]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                {n.interaccion ? (
                  <EstadoDeRespuesta resultado={n.resultado} />
                ) : (
                  <span className={`ll-estado ${n.estado === "encolado" ? "es-aviso" : "es-apagado"}`}>
                    {n.estado === "encolado" ? "Por salir" : n.cuando ? `Escrito ${hora(n.cuando)}` : "Escrito"}
                  </span>
                )}
              </div>
              {n.dijo && <div className="ll-dijo">«{n.dijo}»</div>}
              <div className="ll-fila">
                <a className="ll-boton-suave" href={`https://wa.me/34${n.telefono}`} target="_blank" rel="noreferrer">
                  Abrir el chat
                </a>
                {n.mensaje && (
                  <button type="button" className="ll-ver" onClick={() => setAbierto(abierto === n.telefono ? null : n.telefono)}>
                    {abierto === n.telefono ? "Ocultar lo que le escribimos" : "Lo que le escribimos"}
                  </button>
                )}
                {(
                  [
                    ["interesado", "Interesado"],
                    ["cliente", "Ya es cliente"],
                    ["no_interesa", "No le interesa"],
                  ] as const
                ).map(([k, t]) => (
                  <button
                    type="button"
                    key={k}
                    className={`ll-boton-suave ${n.resultado === k ? "es-activo" : ""}`}
                    disabled={yendo === n.telefono || n.resultado === k}
                    onClick={() => void marcar(n, k)}
                  >
                    {t}
                  </button>
                ))}
              </div>
              {abierto === n.telefono && n.mensaje && <div className="ll-burbuja">{n.mensaje}</div>}
            </li>
          ))}
        </ul>
      )}
      {fallo && <p className="aviso">{fallo}</p>}
      <p className="pie">
        «Contestado» es que respondió por WhatsApp o que lo marcaste tú. «No le interesa» lo saca de toda campaña
        futura.
      </p>
    </section>
  );
}
